/**
 * IRP defaults, pre-fill for an initial IRP, and the rules checked before
 * signing (mirrors validate_forms.go on the server).
 */
import {
  DLA20_DOMAINS,
  PRP_IRP_DOMAINS,
  type PrpDla20Content,
  type PrpIrpContent,
  type PrpIrpGoal,
  type PrpIrpGoalDraft,
  type PrpIrpReviewGoal,
  type PrpPresentingProblemsContent,
  type PrpReferralContent,
} from "@/api/prp.api";
import { newId, parseOnto } from "./use-draft";

export function emptyIrpGoal(): PrpIrpGoal {
  return {
    id: newId(),
    domain: "",
    status: "new",
    client_words: "",
    clinical_goal: "",
    target_date: "",
    short_term_goal: "",
    interventions: { client: [""], rc: [""], therapist: [] },
    why: "",
    when: "",
    review_assessment: "",
    intervention_changed: null,
  };
}

export function emptyIrp(): PrpIrpContent {
  return {
    irp_type: "initial",
    previous_irp_id: "",
    site: "",
    strengths_abilities: "",
    needs_preferences: "",
    recovery_expectations: "",
    integration_inclusion: "",
    sud: {
      has_challenge: null,
      referral_made: null,
      plan_if_no_referral: "",
      ongoing_communication: null,
      no_communication_reason: "",
      feedback_from: "",
      feedback_summary: "",
    },
    goals: [emptyIrpGoal()],
    services: [],
    external_supports: [],
    transition_plan: { target_date: "", objectives_met: null, condition: "" },
    discharge_criteria: { target_date: "", objectives_met: null, condition: "" },
    diagnoses: [{ code: "", description: "", primary: true }],
    diagnosis_changed: null,
    participation_level: "",
    housing_level: "",
    how_treatment_supports: "",
    copy_received: "",
    safety_plan: { needed: null, reason: "" },
  };
}

export function parseIrp(raw?: string): PrpIrpContent {
  const base = emptyIrp();
  const c = parseOnto(raw, base);
  return {
    ...c,
    sud: { ...base.sud, ...(c.sud ?? {}) },
    goals: (c.goals ?? []).map((g) => ({
      ...emptyIrpGoal(),
      ...g,
      interventions: {
        client: g.interventions?.client ?? [],
        rc: g.interventions?.rc ?? [],
        therapist: g.interventions?.therapist ?? [],
      },
    })),
    services: c.services ?? [],
    external_supports: c.external_supports ?? [],
    transition_plan: { ...base.transition_plan, ...(c.transition_plan ?? {}) },
    discharge_criteria: { ...base.discharge_criteria, ...(c.discharge_criteria ?? {}) },
    diagnoses: c.diagnoses?.length ? c.diagnoses : base.diagnoses,
    safety_plan: { ...base.safety_plan, ...(c.safety_plan ?? {}) },
  };
}

const joinText = (...parts: (string | undefined)[]) =>
  parts.filter((p) => p && p.trim()).join("\n\n");

/** DLA-20 domain -> IRP goal domain, for goal suggestions. */
const DLA_TO_IRP_DOMAIN: Record<string, string> = {
  health_practices: "mental_health",
  housing: "housing",
  communication: "social_skills",
  safety: "crisis_support",
  managing_time: "independent_living",
  managing_money: "independent_living",
  nutrition: "independent_living",
  problem_solving: "independent_living",
  family_relationships: "social_skills",
  alcohol_drug: "mental_health",
  leisure: "community_integration",
  community_resources: "community_integration",
  social_network: "social_skills",
  sexual_health: "physical_health",
  productivity: "vocational",
  coping_skills: "mental_health",
  behavioral_norms: "community_integration",
  personal_hygiene: "independent_living",
  grooming: "independent_living",
  dress: "independent_living",
};

function fmtUS(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return m + "/" + d + "/" + y;
}

/** Initial IRP pre-filled from the referral, Presenting Problems and DLA-20. */
export function irpFromSources(
  referral: PrpReferralContent | null,
  pp: PrpPresentingProblemsContent | null,
  dla: { content: PrpDla20Content; date: string } | null = null,
): PrpIrpContent {
  const c = emptyIrp();
  if (referral) {
    const coded = referral.diagnoses.filter((d) => d.code.trim());
    if (coded.length) c.diagnoses = coded.map((d) => ({ ...d }));
  }
  if (pp) {
    c.strengths_abilities = joinText(pp.strengths, pp.client_abilities);
    c.needs_preferences = joinText(pp.client_needs, pp.client_preferences);
    c.recovery_expectations = pp.treatment_expectations;
    const ltgs = pp.long_term_goals.filter((g) => g.goal.trim());
    if (ltgs.length) {
      c.goals = ltgs.map((g, i) => ({
        ...emptyIrpGoal(),
        client_words: g.goal,
        target_date: g.target_date,
        short_term_goal: pp.short_term_goals[i]?.goal ?? "",
      }));
    }
  }
  if (dla) {
    const scored = DLA20_DOMAINS.map((d) => ({
      ...d,
      score: dla.content.items[d.key]?.score ?? 0,
    })).filter((d) => d.score > 0);
    const low = scored.filter((d) => d.score <= 4).sort((a, b) => a.score - b.score);
    const high = scored.filter((d) => d.score >= 5).sort((a, b) => b.score - a.score);
    const tag = "DLA-20 (" + fmtUS(dla.date) + ")";
    const list = (xs: { name: string; score: number }[]) =>
      xs.map((d) => d.name + " (" + d.score + ")").join(", ");
    if (high.length)
      c.strengths_abilities = joinText(c.strengths_abilities, tag + " strengths: " + list(high));
    if (low.length)
      c.needs_preferences = joinText(c.needs_preferences, tag + " impairments: " + list(low));
    // No goals from Presenting Problems: suggest one per serious impairment (score 3 or lower).
    if (!c.goals.some((g) => g.client_words.trim())) {
      const suggested = low.filter((d) => d.score <= 3).slice(0, 4);
      if (suggested.length) {
        c.goals = suggested.map((d) => ({
          ...emptyIrpGoal(),
          domain: DLA_TO_IRP_DOMAIN[d.key] ?? "",
          clinical_goal: "Improve " + d.name.toLowerCase() + " (DLA-20 score " + d.score + ")",
        }));
      }
    }
    const safety = dla.content.items.safety?.score ?? 0;
    if (safety >= 1 && safety <= 3) {
      c.safety_plan = {
        needed: true,
        reason: "DLA-20 Safety scored " + safety + " on " + fmtUS(dla.date) + ".",
      };
    }
  }
  return c;
}

const blank = (s?: string) => !s || s.trim() === "";
const isDate = (s?: string) => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);
const ACTIVE = new Set(["new", "continue", "revised"]);
const DOMAINS = new Set(PRP_IRP_DOMAINS.map((d) => d.key));

export function irpProblems(c: PrpIrpContent): string[] {
  const p: string[] = [];
  const review = c.irp_type === "review";
  if (c.irp_type !== "initial" && !review) p.push("Initial IRP or IRP Review");
  if (blank(c.strengths_abilities)) p.push("Strengths and abilities");
  if (blank(c.needs_preferences)) p.push("Needs and preferences");
  if (blank(c.recovery_expectations)) p.push("Recovery and treatment expectations");

  if (c.sud.has_challenge === null) p.push("Substance use challenge: yes or no");
  else if (c.sud.has_challenge) {
    if (c.sud.referral_made === null) p.push("Substance use: has a referral been made?");
    else if (!c.sud.referral_made && blank(c.sud.plan_if_no_referral))
      p.push("Substance use: plan when no referral is made");
    if (c.sud.ongoing_communication === null)
      p.push("Substance use: ongoing communication with the provider?");
    else if (!c.sud.ongoing_communication && blank(c.sud.no_communication_reason))
      p.push("Substance use: reason for no communication");
  }

  let active = 0;
  c.goals.forEach((g, i) => {
    const n = `Goal ${i + 1}`;
    if (!ACTIVE.has(g.status)) {
      if (review && blank(g.review_assessment)) p.push(`${n}: review assessment`);
      return;
    }
    active++;
    if (!DOMAINS.has(g.domain)) p.push(`${n}: domain`);
    if (blank(g.client_words)) p.push(`${n}: goal in the client's own words`);
    if (blank(g.clinical_goal)) p.push(`${n}: clinical goal`);
    if (!isDate(g.target_date)) p.push(`${n}: target date`);
    if (blank(g.short_term_goal)) p.push(`${n}: short-term goal`);
    if (!g.interventions.client.some((x) => !blank(x)))
      p.push(`${n}: at least one "Client will" step`);
    if (!g.interventions.rc.some((x) => !blank(x)))
      p.push(`${n}: at least one "RC will" intervention`);
    if (review && g.status !== "new") {
      if (blank(g.review_assessment)) p.push(`${n}: review assessment`);
      if (g.intervention_changed === null) p.push(`${n}: has the intervention changed?`);
    }
  });
  if (!active) p.push("At least one active goal");

  if (c.diagnoses.filter((d) => !blank(d.code) && d.primary).length !== 1)
    p.push("Treatment diagnoses with exactly one primary");
  if (review && c.diagnosis_changed === null)
    p.push("Has the diagnosis changed from the prior plan?");
  if (blank(c.how_treatment_supports))
    p.push("How the plan supports symptom management and recovery");
  if (blank(c.discharge_criteria.condition)) p.push("Discharge criteria");
  if (c.safety_plan.needed === null) p.push("Personal Safety Plan: create/revise needed?");
  else if (c.safety_plan.needed && blank(c.safety_plan.reason))
    p.push("Personal Safety Plan: reason");
  return p;
}

const hasSteps = (xs: string[]) => xs.some((x) => x.trim());

/** Fills only the blank fields of a goal from an AI completion. */
export function completeIrpGoal(g: PrpIrpGoal, d: PrpIrpGoalDraft): PrpIrpGoal {
  return {
    ...g,
    domain: g.domain || d.domain || "",
    client_words: g.client_words.trim() ? g.client_words : d.client_words || g.client_words,
    clinical_goal: g.clinical_goal.trim() ? g.clinical_goal : d.clinical_goal || g.clinical_goal,
    target_date: g.target_date || d.target_date || "",
    short_term_goal: g.short_term_goal.trim()
      ? g.short_term_goal
      : d.short_term_goal || g.short_term_goal,
    interventions: {
      ...g.interventions,
      client: hasSteps(g.interventions.client)
        ? g.interventions.client
        : d.client_will?.length
          ? d.client_will
          : g.interventions.client,
      rc: hasSteps(g.interventions.rc)
        ? g.interventions.rc
        : d.rc_will?.length
          ? d.rc_will
          : g.interventions.rc,
    },
    why: g.why.trim() ? g.why : d.why || g.why,
    when: g.when.trim() ? g.when : d.when || g.when,
  };
}

/** A new plan goal from an AI suggestion. */
export function irpGoalFromDraft(d: PrpIrpGoalDraft): PrpIrpGoal {
  return completeIrpGoal(emptyIrpGoal(), d);
}

/** Applies an AI review suggestion: status, plus the assessment and
 * "intervention changed" only where blank; a revised goal gets the new short-term goal. */
export function applyIrpReview(g: PrpIrpGoal, r: PrpIrpReviewGoal): PrpIrpGoal {
  if (!r.status) return g;
  const closed = r.status === "met" || r.status === "discontinued";
  return {
    ...g,
    status: r.status,
    review_assessment: g.review_assessment.trim() ? g.review_assessment : r.review_assessment || "",
    intervention_changed: closed
      ? g.intervention_changed
      : (g.intervention_changed ?? r.intervention_changed ?? null),
    short_term_goal:
      r.status === "revised" && r.revised_short_term_goal
        ? r.revised_short_term_goal
        : g.short_term_goal,
  };
}
